export const CLI_DEVICE_AUTH_PATH = "/api/auth/hosted/device";

export const CLI_DEVICE_SCOPES = [
  "workspace:read",
  "account:read",
  "credits:read",
  "providers:read",
  "offers:read",
  "compute:read",
  "compute:plan",
  "compute:launch",
  "compute:terminate",
] as const;

export type CLIDeviceScope = (typeof CLI_DEVICE_SCOPES)[number];
export type CLIDeviceWorkspaceRole = "viewer" | "admin" | "owner";
export type CLIDeviceDecisionStatus = "approved" | "denied";
export type CLIDeviceAuthErrorKind =
  | "session_required"
  | "invalid_code"
  | "rate_limited"
  | "unavailable";

export type CLIDeviceWorkspace = {
  id: string;
  name: string;
  role: CLIDeviceWorkspaceRole;
};

export type CLIDeviceSessionUser = {
  id: string;
  email: string;
  name: string;
};

export type CLIDeviceAuthorization = {
  clientId: "genrl-cli";
  resource: string;
  scopes: CLIDeviceScope[];
  expiresIn: number;
  sessionUser: CLIDeviceSessionUser;
  workspaces: CLIDeviceWorkspace[];
};

export type CLIDeviceFetch = (
  input: string,
  init: RequestInit,
) => Promise<Response>;

const ERROR_MESSAGES: Record<CLIDeviceAuthErrorKind, string> = {
  session_required: "Sign in to continue.",
  invalid_code:
    "This code is invalid or has expired. Start a new login from your terminal.",
  rate_limited: "Too many attempts. Wait a minute and try again.",
  unavailable: "GENRL could not verify this request. Try again.",
};

const SCOPE_SET = new Set<string>(CLI_DEVICE_SCOPES);
const WORKSPACE_ROLES = new Set<string>(["viewer", "admin", "owner"]);

export class CLIDeviceAuthError extends Error {
  readonly kind: CLIDeviceAuthErrorKind;

  constructor(kind: CLIDeviceAuthErrorKind) {
    super(ERROR_MESSAGES[kind]);
    this.name = "CLIDeviceAuthError";
    this.kind = kind;
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function boundedString(value: unknown, maxLength: number): string | null {
  if (typeof value !== "string" || value.length === 0 || value.length > maxLength) {
    return null;
  }
  if (value !== value.trim() || /[\u0000-\u001f\u007f]/.test(value)) return null;
  return value;
}

function validWorkspaceID(value: unknown): string | null {
  const id = boundedString(value, 160);
  if (!id || /[\s*/]/.test(id)) return null;
  return id;
}

function validSessionUserID(value: unknown): string | null {
  const id = boundedString(value, 160);
  return id && !/\s/.test(id) ? id : null;
}

function validSessionUser(value: unknown): CLIDeviceSessionUser | null {
  const user = asRecord(value);
  const id = validSessionUserID(user?.id);
  const email = boundedString(user?.email, 320);
  const name = boundedString(user?.name, 160);
  if (
    !id ||
    !email ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ||
    !name
  ) {
    return null;
  }
  return { id, email, name };
}

function validResource(value: unknown): string | null {
  const resource = boundedString(value, 2048);
  if (!resource) return null;
  try {
    const parsed = new URL(resource);
    const loopback =
      parsed.hostname === "localhost" ||
      parsed.hostname === "127.0.0.1" ||
      parsed.hostname === "[::1]";
    if (parsed.protocol !== "https:" && !(parsed.protocol === "http:" && loopback)) {
      return null;
    }
    if (
      parsed.username ||
      parsed.password ||
      parsed.hash ||
      parsed.search ||
      parsed.pathname !== "/" ||
      resource.endsWith("/")
    ) {
      return null;
    }
    return resource;
  } catch {
    return null;
  }
}

export function normalizeCLIDeviceUserCode(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const compact = raw.toUpperCase().replace(/[-\s]/g, "");
  if (!/^[A-Z]{8}$/.test(compact)) return null;
  return `${compact.slice(0, 4)}-${compact.slice(4)}`;
}

export function prepareCLIDeviceSignIn(
  location: Pick<Location, "pathname" | "search" | "hash">,
  rememberReturnTo: (returnTo: string) => void,
): "/ui/login" {
  const pathname = location.pathname === "/device" ? "/ui/device" : location.pathname;
  rememberReturnTo(`${pathname}${location.search}${location.hash}`);
  return "/ui/login";
}

export function projectCLIDeviceAuthorization(
  payload: unknown,
): CLIDeviceAuthorization {
  const record = asRecord(payload);
  if (!record || record.client_id !== "genrl-cli") {
    throw new CLIDeviceAuthError("unavailable");
  }

  const resource = validResource(record.resource);
  const sessionUser = validSessionUser(record.session_user);
  const rawScope = boundedString(record.scope, 512);
  const expiresIn = record.expires_in;
  const rawWorkspaces = record.workspaces;
  if (
    !resource ||
    !sessionUser ||
    !rawScope ||
    typeof expiresIn !== "number" ||
    !Number.isInteger(expiresIn) ||
    expiresIn < 1 ||
    expiresIn > 600 ||
    !Array.isArray(rawWorkspaces) ||
    rawWorkspaces.length > 100
  ) {
    throw new CLIDeviceAuthError("unavailable");
  }

  const rawScopes = rawScope.split(/\s+/);
  const scopeSet = new Set(rawScopes);
  if (
    rawScopes.length === 0 ||
    scopeSet.size !== rawScopes.length ||
    rawScopes.some((scope) => !SCOPE_SET.has(scope))
  ) {
    throw new CLIDeviceAuthError("unavailable");
  }
  const scopes = CLI_DEVICE_SCOPES.filter((scope) => scopeSet.has(scope));

  const workspaceIDs = new Set<string>();
  const workspaces = rawWorkspaces.map((value): CLIDeviceWorkspace => {
    const workspace = asRecord(value);
    const id = validWorkspaceID(workspace?.id);
    const name = boundedString(workspace?.name, 160);
    const role = boundedString(workspace?.role, 16);
    if (!id || !name || !role || !WORKSPACE_ROLES.has(role) || workspaceIDs.has(id)) {
      throw new CLIDeviceAuthError("unavailable");
    }
    workspaceIDs.add(id);
    return { id, name, role: role as CLIDeviceWorkspaceRole };
  });

  return {
    clientId: "genrl-cli",
    resource,
    scopes,
    expiresIn,
    sessionUser,
    workspaces,
  };
}

function errorForStatus(status: number): CLIDeviceAuthError {
  if (status === 401) return new CLIDeviceAuthError("session_required");
  if (status === 400 || status === 404 || status === 409) {
    return new CLIDeviceAuthError("invalid_code");
  }
  if (status === 429) return new CLIDeviceAuthError("rate_limited");
  return new CLIDeviceAuthError("unavailable");
}

async function safeJSON(response: Response): Promise<unknown> {
  if (!(response.headers.get("Content-Type") || "").toLowerCase().includes("application/json")) {
    throw new CLIDeviceAuthError("unavailable");
  }
  try {
    return await response.json();
  } catch {
    throw new CLIDeviceAuthError("unavailable");
  }
}

function activeFetcher(fetcher?: CLIDeviceFetch): CLIDeviceFetch {
  if (fetcher) return fetcher;
  return (input, init) => globalThis.fetch(input, init);
}

export async function loadCLIDeviceAuthorization(
  userCode: string,
  fetcher?: CLIDeviceFetch,
): Promise<CLIDeviceAuthorization> {
  const normalized = normalizeCLIDeviceUserCode(userCode);
  if (!normalized) throw new CLIDeviceAuthError("invalid_code");

  let response: Response;
  try {
    response = await activeFetcher(fetcher)(
      `${CLI_DEVICE_AUTH_PATH}?user_code=${encodeURIComponent(normalized)}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
        cache: "no-store",
      },
    );
  } catch (error) {
    if (error instanceof CLIDeviceAuthError) throw error;
    throw new CLIDeviceAuthError("unavailable");
  }
  if (!response.ok) throw errorForStatus(response.status);
  return projectCLIDeviceAuthorization(await safeJSON(response));
}

async function decideCLIDeviceAuthorization(
  userCode: string,
  decision: "approve" | "deny",
  workspaceID: string,
  assertedSessionUserID: string | undefined,
  fetcher?: CLIDeviceFetch,
): Promise<CLIDeviceDecisionStatus> {
  const normalized = normalizeCLIDeviceUserCode(userCode);
  const normalizedWorkspaceID =
    decision === "approve" ? validWorkspaceID(workspaceID) : "";
  const expectedSessionUserID =
    decision === "approve" ? validSessionUserID(assertedSessionUserID) : null;
  if (!normalized || normalizedWorkspaceID === null) {
    throw new CLIDeviceAuthError("invalid_code");
  }
  if (decision === "approve" && !expectedSessionUserID) {
    throw new CLIDeviceAuthError("unavailable");
  }

  let response: Response;
  try {
    response = await activeFetcher(fetcher)(CLI_DEVICE_AUTH_PATH, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      cache: "no-store",
      body: JSON.stringify({
        user_code: normalized,
        decision,
        workspace_id: normalizedWorkspaceID,
        ...(expectedSessionUserID
          ? { expected_session_user_id: expectedSessionUserID }
          : {}),
      }),
    });
  } catch (error) {
    if (error instanceof CLIDeviceAuthError) throw error;
    throw new CLIDeviceAuthError("unavailable");
  }
  if (!response.ok) throw errorForStatus(response.status);

  const result = asRecord(await safeJSON(response));
  const expectedStatus: CLIDeviceDecisionStatus =
    decision === "approve" ? "approved" : "denied";
  if (!result || result.status !== expectedStatus) {
    throw new CLIDeviceAuthError("unavailable");
  }
  return result.status as CLIDeviceDecisionStatus;
}

export function approveCLIDeviceAuthorization(
  userCode: string,
  workspaceID: string,
  expectedSessionUserID: string,
  fetcher?: CLIDeviceFetch,
): Promise<CLIDeviceDecisionStatus> {
  return decideCLIDeviceAuthorization(
    userCode,
    "approve",
    workspaceID,
    expectedSessionUserID,
    fetcher,
  );
}

export function denyCLIDeviceAuthorization(
  userCode: string,
  fetcher?: CLIDeviceFetch,
): Promise<CLIDeviceDecisionStatus> {
  return decideCLIDeviceAuthorization(userCode, "deny", "", undefined, fetcher);
}
